import { useState, useEffect } from 'react';
import {
  Panel,
  PanelType,
  Text,
  Icon,
  IconButton,
  Spinner,
  SpinnerSize,
  PrimaryButton,
  DefaultButton,
  IContextualMenuItem,
} from '@fluentui/react';
import { useFileBrowserStore } from '@/hooks';
import { config, formatFileSize, formatDateFull } from '@/config';
import { FileIcon } from './FileIcon';
import { sharePointService } from '@/services/sharePointService';
import { emailService } from '@/services/emailService';
import { logger } from '@/utils/logger';
import { getPreviewKind } from '@/utils/fileTypes';

export function PreviewPanel() {
  const {
    previewItem,
    previewMetadata,
    previewContent,
    previewHtml,
    isPreviewLoading,
    setPreviewItem,
    downloadItem,
    openFile,
    attachItemToEmail,
  } = useFileBrowserStore();

  // Compose vs read is detected once (same as the toolbar): the compose-only
  // "Attach to Email" action is the only thing that needs it.
  const [isComposeMode, setIsComposeMode] = useState(false);

  useEffect(() => {
    setIsComposeMode(emailService.isComposeMode());
  }, []);

  // Server-rendered preview (Office/PDF): short-lived embeddable Graph URL
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  // Media preview (image/video/audio): object URL of the downloaded blob
  const [mediaBlobUrl, setMediaBlobUrl] = useState<string | null>(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);

  const kind = previewItem ? getPreviewKind(previewItem.name) : 'other';
  const fitsPreviewSize = (previewItem?.size || 0) <= config.features.maxPreviewSize;

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (mediaBlobUrl) {
        URL.revokeObjectURL(mediaBlobUrl);
      }
    };
  }, [mediaBlobUrl]);

  // Reset transient state, then auto-load whatever the type supports:
  // Office/PDF fetch the server-side preview URL, images load inline.
  useEffect(() => {
    setEmbedUrl(null);
    setDocumentError(null);
    setIsLoadingDocument(false);
    if (mediaBlobUrl) {
      URL.revokeObjectURL(mediaBlobUrl);
      setMediaBlobUrl(null);
    }
    if (!previewItem) return;

    const itemKind = getPreviewKind(previewItem.name);
    if (itemKind === 'office' || itemKind === 'pdf') {
      loadEmbedPreview(previewItem.path);
    } else if (itemKind === 'image' && (previewItem.size || 0) <= config.features.maxPreviewSize) {
      loadMediaFile(previewItem.path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewItem?.path]);

  if (!previewItem) return null;

  const onClose = () => {
    setPreviewItem(null);
    setDocumentError(null);
    if (mediaBlobUrl) {
      URL.revokeObjectURL(mediaBlobUrl);
      setMediaBlobUrl(null);
    }
    setEmbedUrl(null);
  };

  // Open the full item in SharePoint (store action prefers webUrl).
  const openItem = () => {
    openFile(previewItem);
  };

  // Header actions, frequency-ordered (primary first). Attach is compose-only
  // but stays visible (disabled) in read mode so the action is discoverable,
  // matching the toolbar convention.
  const previewMenuItems: IContextualMenuItem[] = [
    {
      key: 'open',
      text: 'Open in SharePoint',
      iconProps: { iconName: 'OpenInNewWindow' },
      onClick: () => {
        openItem();
      },
    },
    {
      key: 'download',
      text: 'Download',
      iconProps: { iconName: 'Download' },
      onClick: () => {
        downloadItem(previewItem);
      },
    },
  ];
  previewMenuItems.push({
    key: 'attach',
    text: 'Attach to Email',
    iconProps: { iconName: 'Attach' },
    disabled: !isComposeMode,
    title: isComposeMode ? undefined : 'Available while composing an email',
    onClick: () => {
      void attachItemToEmail(previewItem);
    },
  });

  // Fetch the short-lived embeddable preview URL (Office/PDF, server-rendered)
  const loadEmbedPreview = async (path: string) => {
    setIsLoadingDocument(true);
    setDocumentError(null);
    try {
      const url = await sharePointService.getPreviewUrl(path);
      setEmbedUrl(url);
    } catch (error) {
      logger.warn('Embed preview unavailable:', error);
      setDocumentError('Preview is not available for this file.');
    } finally {
      setIsLoadingDocument(false);
    }
  };

  // Load media file content as a blob (image/video/audio)
  const loadMediaFile = async (path: string) => {
    setIsLoadingDocument(true);
    setDocumentError(null);
    try {
      const blob = await sharePointService.getFileBlob(path);
      const url = URL.createObjectURL(blob);
      setMediaBlobUrl(url);
    } catch (error) {
      logger.error('Error loading media:', error);
      setDocumentError('Failed to load file. Try downloading instead.');
    } finally {
      setIsLoadingDocument(false);
    }
  };

  // Render loading state
  const renderLoading = (label: string) => (
    <div className="preview-loading">
      <Spinner size={SpinnerSize.large} label={label} />
    </div>
  );

  // Fallback: no inline preview - offer the full-read paths instead
  const renderFallback = (hint: string, withOpen = true) => (
    <div className="no-preview">
      <FileIcon name={previewItem.name} isFolder={false} size={64} />
      <Text
        variant="large"
        style={{ marginTop: 16, display: 'block', color: 'var(--text-primary)' }}
      >
        {previewItem.name}
      </Text>
      {documentError && (
        <Text style={{ color: 'var(--error-color)', marginTop: 8 }}>{documentError}</Text>
      )}
      <Text className="no-preview-hint">{hint}</Text>
      <div
        style={{
          marginTop: 20,
          display: 'flex',
          gap: 8,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        {withOpen && (
          <PrimaryButton
            iconProps={{ iconName: 'OpenInNewWindow' }}
            text="Open"
            onClick={openItem}
          />
        )}
        <DefaultButton
          iconProps={{ iconName: 'Download' }}
          text="Download"
          onClick={() => downloadItem(previewItem)}
        />
      </div>
    </div>
  );

  // Render load button (media types load on demand, not automatically)
  const renderLoadButton = (label: string, onLoad: () => void) => (
    <div className="no-preview">
      <FileIcon name={previewItem.name} isFolder={false} size={64} />
      <Text
        variant="large"
        style={{ marginTop: 16, display: 'block', color: 'var(--text-primary)' }}
      >
        {previewItem.name}
      </Text>
      {documentError && (
        <Text style={{ color: 'var(--error-color)', marginTop: 8 }}>{documentError}</Text>
      )}
      <Text className="no-preview-hint">{label}</Text>
      <div
        style={{
          marginTop: 20,
          display: 'flex',
          gap: 8,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        <PrimaryButton iconProps={{ iconName: 'View' }} text="Load Preview" onClick={onLoad} />
        <DefaultButton
          iconProps={{ iconName: 'Download' }}
          text="Download"
          onClick={() => downloadItem(previewItem)}
        />
      </div>
    </div>
  );

  const renderPreviewContent = () => {
    // Office documents + PDF: server-rendered preview in an iframe.
    // The taskpane is narrow, so this is a quick look; Open is the full read.
    if (kind === 'office' || kind === 'pdf') {
      if (isLoadingDocument) return renderLoading('Loading preview');
      if (embedUrl) {
        return (
          <div className="embed-preview">
            <iframe src={embedUrl} title={previewItem.name} className="preview-iframe" />
          </div>
        );
      }
      return renderFallback('Open in SharePoint to view this document.');
    }

    // Image preview (auto-loaded when within the size gate)
    if (kind === 'image') {
      if (isLoadingDocument) return renderLoading('Loading image');
      if (mediaBlobUrl) {
        return (
          <div className="media-preview">
            <img src={mediaBlobUrl} alt={previewItem.name} className="preview-image" />
          </div>
        );
      }
      if (!fitsPreviewSize) {
        return renderFallback('This image is too large to preview.');
      }
      return renderFallback('Unable to preview this image.');
    }

    // Video preview (load on demand - can be large)
    if (kind === 'video') {
      if (isLoadingDocument) return renderLoading('Loading video');
      if (mediaBlobUrl) {
        return (
          <div className="media-preview">
            <video controls className="preview-video" src={mediaBlobUrl}>
              Your browser does not support video playback.
            </video>
          </div>
        );
      }
      return renderLoadButton('Preview video', () => loadMediaFile(previewItem.path));
    }

    // Audio preview (load on demand)
    if (kind === 'audio') {
      if (isLoadingDocument) return renderLoading('Loading audio');
      if (mediaBlobUrl) {
        return (
          <div className="media-preview audio-preview">
            <FileIcon name={previewItem.name} isFolder={false} size={64} />
            <Text
              variant="large"
              style={{ marginTop: 16, marginBottom: 16, color: 'var(--text-primary)' }}
            >
              {previewItem.name}
            </Text>
            <audio controls src={mediaBlobUrl} style={{ width: '100%', maxWidth: 400 }}>
              Your browser does not support audio playback.
            </audio>
          </div>
        );
      }
      return renderLoadButton('Preview audio', () => loadMediaFile(previewItem.path));
    }

    // Email preview (.eml parsed via MIME, .msg via MsgReader)
    if (kind === 'eml' || kind === 'msg') {
      // Still loading
      if (isPreviewLoading) {
        return renderLoading('Loading email');
      }

      // Loaded successfully
      if (previewMetadata) {
        const formatRecipients = (recipients: string | string[] | undefined): string => {
          if (!recipients) return '-';
          if (Array.isArray(recipients)) return recipients.join('; ') || '-';
          return recipients;
        };

        const formatDate = (date: Date | string | undefined): string => {
          if (!date) return '-';
          const d = typeof date === 'string' ? new Date(date) : date;
          return d.toLocaleString(undefined, {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        };

        return (
          <div className="email-natural-view">
            <div className="email-header-section">
              <div className="email-subject-row">
                <Text className="email-subject-text">
                  {previewMetadata.subject || '(No Subject)'}
                </Text>
              </div>
              <div className="email-meta-row">
                <div className="email-meta-item">
                  <span className="email-meta-label">From:</span>
                  <span className="email-meta-value">{previewMetadata.from || '-'}</span>
                </div>
                <div className="email-meta-item">
                  <span className="email-meta-label">To:</span>
                  <span className="email-meta-value">{formatRecipients(previewMetadata.to)}</span>
                </div>
                {previewMetadata.cc && previewMetadata.cc.length > 0 && (
                  <div className="email-meta-item">
                    <span className="email-meta-label">Cc:</span>
                    <span className="email-meta-value">{formatRecipients(previewMetadata.cc)}</span>
                  </div>
                )}
                <div className="email-meta-item">
                  <span className="email-meta-label">Date:</span>
                  <span className="email-meta-value">{formatDate(previewMetadata.date)}</span>
                </div>
              </div>
              {previewMetadata.hasAttachments && (
                <div className="email-attachments-row">
                  <Icon iconName="Attach" />
                  <span>Has attachments</span>
                </div>
              )}
            </div>
            <div className="email-body-section">
              {previewHtml ? (
                // Sandboxed (no scripts, no same-origin); links open in a new
                // tab via the injected <base>. cid: images were resolved to
                // data URLs at parse time.
                <iframe
                  className="email-html-frame"
                  sandbox="allow-popups allow-popups-to-escape-sandbox"
                  title="Email body"
                  srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>body{font-family:'Segoe UI',sans-serif;font-size:14px;margin:8px;word-break:break-word;}img{max-width:100%;height:auto;}</style></head><body>${previewHtml}</body></html>`}
                />
              ) : previewContent ? (
                <div className="email-body-content">{previewContent}</div>
              ) : (
                <Text className="email-no-body">No message content</Text>
              )}
            </div>
            <div className="email-actions">
              <PrimaryButton
                iconProps={{ iconName: 'Download' }}
                text="Download"
                onClick={() => downloadItem(previewItem)}
              />
            </div>
          </div>
        );
      }

      // Too large or failed to parse - offer the full-read paths
      return renderFallback(
        fitsPreviewSize
          ? 'Unable to preview this email. Please download to view.'
          : 'This email is too large to preview.',
      );
    }

    // Text/code preview
    if (kind === 'text') {
      if (isPreviewLoading) {
        return renderLoading('Loading preview');
      }
      if (previewContent) {
        return (
          <div className="text-preview">
            <pre>{previewContent}</pre>
          </div>
        );
      }
      if (!fitsPreviewSize) {
        return renderFallback('This file is too large to preview.');
      }
      return renderFallback('Unable to preview this file.');
    }

    // Default - no preview for this type
    return renderFallback('Preview not available for this file type.');
  };

  return (
    <Panel
      isOpen={!!previewItem}
      onDismiss={onClose}
      type={PanelType.smallFluid}
      headerText="Preview"
      className="preview-panel"
      isLightDismiss
      styles={{
        main: { background: 'var(--bg-primary)' },
        commands: { background: 'var(--bg-primary)' },
        content: { background: 'var(--bg-primary)' },
      }}
    >
      <div className="preview-header">
        <div className="preview-file-info">
          <FileIcon name={previewItem.name} isFolder={false} size={32} />
          <div className="preview-file-details">
            <Text className="preview-filename" title={previewItem.name}>
              {previewItem.name}
            </Text>
            <Text className="preview-meta">
              {formatFileSize(previewItem.size || 0)}
              {previewItem.lastModified && ` • ${formatDateFull(previewItem.lastModified)}`}
            </Text>
          </div>
        </div>
        <div className="preview-actions">
          <IconButton
            iconProps={{ iconName: 'More' }}
            title="More actions"
            ariaLabel="More actions"
            styles={{
              // The three-dot glyph reads fainter than fuller glyphs at the
              // same size, so it gets the primary text color, not theme blue.
              icon: { fontSize: 16, color: 'var(--text-primary)' },
              iconHovered: { color: 'var(--text-primary)' },
              iconPressed: { color: 'var(--text-primary)' },
              iconExpanded: { color: 'var(--text-primary)' },
              menuIcon: { display: 'none' },
            }}
            menuProps={{ items: previewMenuItems }}
          />
        </div>
      </div>

      <div className="preview-content">{renderPreviewContent()}</div>
    </Panel>
  );
}
